import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Breadcrumbs,
    Chip,
    CircularProgress,
    Container,
    IconButton,
    Link,
    Paper,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
    Grid,
    Avatar,
    Stack,
    Button,
    // --- Modal/Form Imports ---
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    SelectChangeEvent,
    useTheme,
    // --- End Modal/Form Imports ---
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from "../../helpers/AuthProvider";
// --- Formik and Yup Imports ---
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from 'formik';
import * as Yup from 'yup';
// --- End Formik ---

import queryGraphql from "../../helpers/ApiInterface";
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../MainDrawer"; // Adjust path if needed
import CompulsoryFieldsLabel from "../../helpers/CompulsoryFields";
import MiniBot from "../MiniBot";
// --- Define paths for custom SVG icons ---
const PASSED_ICON_PATH = "/assets/checkbox-circle-fill.svg";
const FAILED_ICON_PATH = "/assets/close-circle-fill.svg";
const MIRRORING_APP_LOGO_PATH = "/assets/flip-horizontal-line.svg";

// --- Accordion Arrow Icons ---
const ARROW_RIGHT_ICON_PATH = "/assets/arrow-right-s-line.svg";
const ARROW_DOWN_ICON_PATH = "/assets/arrow-down.svg";
const ICON_CONTAINER_BG_COLOR = '#F2F4F7';

// --- Define specific status colors ---
const CHIP_COLOR_GREEN = '#12B76A';
const CHIP_COLOR_RED = '#F04438'; // Used for error text and asterisks
const CHIP_COLOR_BLUE = '#0277BD'; // Used for focus border

// --- Mirroring App Interface (Existing Data) ---
interface MirroringApp {
    id: string;
    name: string;
    sourceRepo: string;
    destinationRepo: string;
    branchMode: string;
    branchName: string;
    tagMode: string;
    tagName: string;
    status: 'passed' | 'failed' | string;
    addedOn?: string | number | null;
}

// --- Mirroring App Input Interface (For Modal Form & Mutation) ---
interface MirroringAppInput {
    name: string;
    sourceRepo: string;
    destinationRepo: string;
    token: string;
    branchMode: string;
    branchName: string;
    tagMode: string;
    tagName: string;
}

// --- Validation Schema using Yup ---
const validationSchema = Yup.object().shape({
    name: Yup.string().required("This field is required").matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric"),
    sourceRepo: Yup.string()
        .url("Must be a valid URL") // Ensures it's a valid URL format
        .matches(/\.git$/, "URL must end with .git") // Ensures it ends with .git
        .required("This field is required"),
    destinationRepo: Yup.string()
        .url("Must be a valid URL") // Ensures it's a valid URL format
        .matches(/\.git$/, "URL must end with .git") // Ensures it ends with .git
        .required("This field is required"),
    token: Yup.string().required("This field is required"),
    branchMode: Yup.string().required("This field is required"),
    branchName: Yup.string().when('branchMode', {
        is: 'custom',
        then: (schema) => schema.required('This field is required when mode is "Specific Branch"'),
        otherwise: (schema) => schema.notRequired(),
    }),
    tagMode: Yup.string().required("This field is required"),
    tagName: Yup.string().when('tagMode', {
        is: 'custom',
        then: (schema) => schema.required('This field is required when mode is "Specific Tag"'),
        otherwise: (schema) => schema.notRequired(),
    }),
});

// --- Initial Form Values ---
const initialFormValues: MirroringAppInput = {
    name: '',
    sourceRepo: '',
    destinationRepo: '',
    token: '',
    branchMode: '',
    branchName: '',
    tagMode: '',
    tagName: ''
};

// Helper for styled error messages
const StyledErrorMessage = (props: { name: string }) => (
    <ErrorMessage name={props.name}>
        {msg => <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, fontFamily: 'Gilroy-Regular' }}>{msg}</Typography>}
    </ErrorMessage>
);

// Helper for labels with asterisk
const CustomFormLabel = ({ htmlFor, label, required }: { htmlFor: string, label: string, required?: boolean }) => (
    <InputLabel shrink htmlFor={htmlFor} sx={{ fontFamily: 'Gilroy-SemiBold', color: '#333', mb: 0.5, fontSize: '0.875rem' }}>
        {label} {required && <span style={{ color: CHIP_COLOR_RED }}>*</span>}
    </InputLabel>
);


// --- React Component ---
const MirroringAppDetailsContent: React.FC = () => {
    const navigate = useNavigate();

    // --- State Variables ---
    const [mirroringAppsData, setMirroringAppsData] = useState<MirroringApp[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [listError, setListError] = useState<string | null>(null);
    const [expandedRepoId, setExpandedRepoId] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const { user } = useAuth();
    const isViewer =
        user.roles?.some((role: string) => role === "viewer") || false;
    const getMirroringAppsQuery = ` query GetMirroringApps { getMirroringApps { branchMode branchName destinationRepo id name sourceRepo status tagMode tagName } } `;
    const addMirroringAppMutation = ` mutation AddMirroringApp($input: MirroringAppResultsInput) { addMirroringApp(input: $input) { id name sourceRepo destinationRepo branchMode branchName tagMode tagName status } } `;

    const fetchData = async () => {
        setIsLoading(true);
        setListError(null);
        try {
            const response = await queryGraphql(getMirroringAppsQuery);
            const fetchedData = response?.getMirroringApps;
            if (Array.isArray(fetchedData)) {
                const dataWithMockDate = fetchedData.map((item, index) => ({
                    ...item,
                    addedOn: item.addedOn || new Date(Date.now() - (index * Math.random() * 5 * 24 * 60 * 60 * 1000)).toISOString(),
                }));
                dataWithMockDate.sort((a, b) => (new Date(b.addedOn || 0)).getTime() - (new Date(a.addedOn || 0)).getTime());
                setMirroringAppsData(dataWithMockDate);
            } else if (response && response.errors) {
                setListError(`GraphQL Error: ${response.errors[0]?.message || 'Unknown GraphQL error'}`);
                setMirroringAppsData([]);
            } else {
                setListError("Could not fetch Mirroring App data or the format is incorrect.");
                setMirroringAppsData([]);
            }
        } catch (err: any) {
            setListError(err.message || "An unexpected error occurred.");
            setMirroringAppsData([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAccordionChange = (repoId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedRepoId(isExpanded ? repoId : null);
    };

    const handleAddRepositoryClick = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleFormSubmit = async (values: MirroringAppInput, { setSubmitting, resetForm, setStatus }: FormikHelpers<MirroringAppInput>) => {
        setStatus(null);
        const input = { ...values };
        try {
            const response = await queryGraphql(addMirroringAppMutation, { input });
            if (response && response.addMirroringApp && response.addMirroringApp.id) {
                resetForm();
                handleCloseModal();
                await fetchData();
            } else if (response && response.errors) {
                setStatus({ error: `Error adding repository: ${response.errors[0]?.message || 'Unknown GraphQL error'}` });
            } else {
                setStatus({ error: "Failed to add repository. Unknown error occurred." });
            }
        } catch (err: any) {
            setStatus({ error: `Submission failed: ${err.message || 'An unexpected error occurred.'}` });
        }
    };

    const formatDate = (timestamp: string | number | null | undefined): string => {
        if (timestamp === null || timestamp === undefined) return "N/A";
        let date: Date | null = null;
        try {
            if (typeof timestamp === 'number') { date = new Date(timestamp); }
            else if (typeof timestamp === 'string') {
                const nTs = Number(timestamp);
                if (!isNaN(nTs) && timestamp.length > 5 && !timestamp.includes('-') && !timestamp.includes('/')) { date = new Date(nTs); }
                else { date = new Date(timestamp); }
            }
            if (date && !isNaN(date.getTime())) { return date.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }); }
            else { return "Invalid Date"; }
        } catch (e) { return "Invalid Date"; }
    };

    const { successfulCount, failedCount } = useMemo(() => {
        return mirroringAppsData.reduce((counts, repo) => {
            if (repo.status?.toLowerCase() === 'passed') { counts.successfulCount++; }
            else if (repo.status?.toLowerCase() === 'failed') { counts.failedCount++; }
            return counts;
        }, { successfulCount: 0, failedCount: 0 });
    }, [mirroringAppsData]);

    const installationDate = useMemo(() => new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), []);

    const commonInputStyles = (errors: any, touched: any, fieldName: string) => ({
        variant: "filled" as "filled",
        hiddenLabel: true,
        size: "small" as "small",
        fullWidth: true,
        InputProps: {
            disableUnderline: true,
            sx: {
                borderRadius: "8px",
                '&.Mui-focused': {
                    backgroundColor: '#FFFFFF',
                },
            }
        },
        sx: {
            "& .MuiFilledInput-root": {
                border: errors[fieldName] && touched[fieldName] ? `1px solid ${CHIP_COLOR_RED}` : "1px solid #E0E0E0",
                backgroundColor: '#F9FAFB',
                borderRadius: "8px",
                '&:hover': {
                    backgroundColor: '#F0F2F5',
                    borderColor: errors[fieldName] && touched[fieldName] ? CHIP_COLOR_RED : '#BDBDBD',
                },
                "&.Mui-focused": {
                    backgroundColor: '#FFFFFF',
                    border: `1px solid ${CHIP_COLOR_BLUE}`,
                },
                '&::before, &::after': {
                    display: 'none',
                },
            },
            "& .MuiFilledInput-input": {
                fontFamily: 'Gilroy-Medium',
                fontSize: '16px',
            },
            "& .MuiSelect-select.MuiFilledInput-input": {
                paddingTop: '10px',
                paddingBottom: '10px',
            },
            '& .MuiFormHelperText-root': {
                fontFamily: 'Gilroy-Regular',
                marginLeft: '2px',
            },
        }
    });


    return (
        <>
            <Container disableGutters maxWidth={false} sx={{ padding: { xs: 2, md: 3 }, bgcolor: "#fafafa", minHeight: "100vh" }} >
                <Box sx={{ mb: 3 }}>
                    <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
                        <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
                        <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Mirroring App </Typography>
                    </Breadcrumbs>
                    <Box display="flex" justifyContent="space-between">
                    <Box display="flex" alignItems="center" flexDirection="row" >
                        <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/integrations")} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
                        <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Mirroring App </Typography>
                    </Box>
                    <IconButton
                                                                                                                          aria-label="Help" 
                                                                                                                          onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
                                                                                                                          sx={{
                                                                                                                            padding: 0, 
                                                                                                                            border: 'none',  
                                                                                                                            ml:2,
                                                                                                                            '&:hover': {
                                                                                                                              
                                                                                                                              backgroundColor: 'action.hover', 
                                                                                                                            },
                                                                                                                            
                                                                                                                          }}
                                                                                                                        >
                                                                                                                          <Box
                                                                                                                            component="img"
                                                                                                                            src="/assets/Help.svg" 
                                                                                                                            alt="" 
                                                                                                                            sx={{
                                                                                                                              width: 40,  
                                                                                                                              height: 40,
                                                                                                                              display: 'block', 
                                                                                                                            }}
                                                                                                                          />
                                                                                                                        </IconButton>
                    </Box>
                </Box>

                <Box sx={{ mx: 'auto' }}>
                    {isLoading && mirroringAppsData.length > 0 && (
                        <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}>
                            <CircularProgress size={30} />
                            <Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Mirroring App Data...</Typography>
                        </Paper>
                    )}
                    {!isLoading && listError && (
                        <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}>
                            <Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> Error loading list: {listError} </Typography>
                        </Paper>
                    )}

                    {!isLoading && !listError && (
                        <>
                            <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}> Review the repositories added for mirroring. </Typography>
                            <Paper elevation={2} sx={{ borderRadius: "12px", p: 2.5, mb: 3, overflow: 'hidden' }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm="auto">
                                        <Avatar alt="Mirroring App Logo" variant="rounded" sx={{ width: 50, height: 50, bgcolor: "#F9F9F9", p: 0.5 }}>
                                            <img src={MIRRORING_APP_LOGO_PATH} alt="" style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain' }} />
                                        </Avatar>
                                    </Grid>
                                    <Grid item xs={12} sm>
                                        <Box display="flex" alignItems="center" mb={0.5} >
                                            <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Mirroring App </Typography>
                                            <Chip label="GIT PROVIDER" size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }} />
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 1 }}> Helps you mirror existing repos to Intellicore gitops workflow. </Typography>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Tooltip title={`Successful Repositories: ${successfulCount}`} placement="top" >
                                                <Chip label={`Successful Repositories: ${successfulCount}`} icon={<Avatar sx={{ width: 18, height: 18, bgcolor: 'transparent', fontFamily: 'Gilroy-Regular' }}> <img src={PASSED_ICON_PATH} alt="" width="18" height="18" style={{ filter: 'brightness(0) invert(1)' }} /> </Avatar>} size="small" sx={{ bgcolor: CHIP_COLOR_GREEN, color: '#fff', height: '28px', borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500, '& .MuiChip-icon': { color: '#fff', ml: '6px', mr: '-2px', width: 18, height: 18 }, '& .MuiChip-label': { pl: '8px', pr: '12px' } }} />
                                            </Tooltip>
                                            <Tooltip title={`Failed Repositories: ${failedCount}`} placement="top">
                                                <Chip label={`Failed Repositories: ${failedCount}`} icon={<Avatar sx={{ width: 18, height: 18, bgcolor: 'transparent' }}> <img src={FAILED_ICON_PATH} alt="" width="18" height="18" style={{ filter: 'brightness(0) invert(1)' }} /> </Avatar>} size="small" sx={{ bgcolor: CHIP_COLOR_RED, color: '#fff', height: '28px', borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500, '& .MuiChip-icon': { color: '#fff', ml: '6px', mr: '-2px', width: 18, height: 18 }, '& .MuiChip-label': { pl: '8px', pr: '12px' } }} />
                                            </Tooltip>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1.5, sm: 0 } }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> Installed On: {formatDate(installationDate.toISOString())} </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>

                            <Box>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} px={1}>
                                    <Typography fontFamily="Gilroy-SemiBold" fontSize="1.1rem"> Repositories ({mirroringAppsData.length}) </Typography>
                                    <Button variant="text" onClick={handleAddRepositoryClick} sx={{ fontFamily: 'Gilroy-SemiBold', textTransform: 'none', fontSize: '0.9rem', color: '#101010' }} > +Add New Repository </Button>
                                </Box>

                                {mirroringAppsData.length === 0 && (
                                    <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', mt: 4, mb: 4 }}> Click "+ Add New Repository" to begin mirroring. </Typography>
                                )}

                                {mirroringAppsData.map((repo) => {
                                    const isExpanded = expandedRepoId === repo.id;
                                    const isFailed = repo.status?.toLowerCase() === 'failed';
                                    return (
                                        <Accordion key={repo.id} expanded={isExpanded} onChange={handleAccordionChange(repo.id)} disableGutters elevation={0} sx={{ border: "1px solid #E0E0E0", borderRadius: "8px !important", boxShadow: 'none', '&:before': { display: 'none' }, mb: 1.5, overflow: 'hidden', bgcolor: '#ffffff' }} >
                                            <AccordionSummary aria-controls={`${repo.id}-content`} id={`${repo.id}-header`} sx={{ px: { xs: 2, md: 2.5 }, py: 0.5, minHeight: '60px', '& .MuiAccordionSummary-content': { display: 'flex', alignItems: 'center', width: '100%', gap: 1.5 } }} >
                                                <Box sx={{ bgcolor: ICON_CONTAINER_BG_COLOR, borderRadius: '6px', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                    <img src={isExpanded ? ARROW_DOWN_ICON_PATH : ARROW_RIGHT_ICON_PATH} alt={isExpanded ? "Collapse" : "Expand"} width="16" height="16" style={{ display: 'block' }} />
                                                </Box>
                                                <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                                                    <Typography fontFamily="Gilroy-SemiBold" sx={{ fontSize: '0.95rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}> {repo.name || 'Unnamed Repository'} </Typography>
                                                    <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', mt: 0.3 }}> Added On: {formatDate(repo.addedOn)} </Typography>
                                                </Box>
                                                <Box sx={{ flexShrink: 0 }}>
                                                    {isFailed && (
                                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                                            <Box sx={{ width: 18, height: 18, bgcolor: CHIP_COLOR_RED, maskImage: `url(${FAILED_ICON_PATH})`, maskSize: 'contain', maskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskImage: `url(${FAILED_ICON_PATH})`, WebkitMaskSize: 'contain', WebkitMaskRepeat: 'no-repeat', WebkitMaskPosition: 'center' }} component="span" />
                                                            <Typography sx={{ color: CHIP_COLOR_RED, fontSize: '0.8rem', fontFamily: 'Gilroy-SemiBold', lineHeight: 1.3 }} > Mirroring Failed </Typography>
                                                        </Stack>
                                                    )}
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ p: 0, bgcolor: '#fdfdfd', borderTop: '1px solid #eee' }}>
                                                <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                                                    <Grid container spacing={2.5} alignItems="flex-start"> {/* Changed alignItems to flex-start for column layout */}
                                                        <Grid item xs={12} md={4}>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                                <Typography variant="caption" component="div" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', whiteSpace: 'nowrap', mb: 0.5 }}>Source Repository URL</Typography>
                                                                <Typography variant="body2" component="div" sx={{ fontFamily: 'Gilroy-Medium', wordBreak: 'break-all' }}>{repo.sourceRepo || 'N/A'}</Typography>
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                                <Typography variant="caption" component="div" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', whiteSpace: 'nowrap', mb: 0.5 }}>Destination Repository URL</Typography>
                                                                <Typography variant="body2" component="div" sx={{ fontFamily: 'Gilroy-Medium', wordBreak: 'break-all' }}>{repo.destinationRepo || 'N/A'}</Typography>
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                                <Typography variant="caption" component="div" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', whiteSpace: 'nowrap', mb: 0.5 }}>Branch Mode</Typography>
                                                                <Typography variant="body2" component="div" sx={{ fontFamily: 'Gilroy-Medium' }}>{repo.branchMode || 'N/A'}</Typography>
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                                <Typography variant="caption" component="div" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', whiteSpace: 'nowrap', mb: 0.5 }}>Branch Name</Typography>
                                                                <Typography variant="body2" component="div" sx={{ fontFamily: 'Gilroy-Medium' }}>{repo.branchName || 'N/A'}</Typography>
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                                <Typography variant="caption" component="div" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', whiteSpace: 'nowrap', mb: 0.5 }}>Tag Mode</Typography>
                                                                <Typography variant="body2" component="div" sx={{ fontFamily: 'Gilroy-Medium' }}>{repo.tagMode || 'N/A'}</Typography>
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                                <Typography variant="caption" component="div" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', whiteSpace: 'nowrap', mb: 0.5 }}>Tag Name</Typography>
                                                                <Typography variant="body2" component="div" sx={{ fontFamily: 'Gilroy-Medium' }}>{repo.tagName || 'N/A'}</Typography>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                })}
                            </Box>
                        </>
                    )}
                </Box>
            </Container>

            <Formik initialValues={initialFormValues} validationSchema={validationSchema} onSubmit={handleFormSubmit} enableReinitialize >
                {({ errors, touched, values, handleChange, handleBlur, isSubmitting, isValid, dirty, status, setFieldValue }) => (
                    <Dialog open={isModalOpen} onClose={() => !isSubmitting && handleCloseModal()} maxWidth="md" fullWidth PaperProps={{ component: Form }} aria-labelledby="add-repository-dialog-title" >
                        <DialogTitle id="add-repository-dialog-title" sx={{ fontFamily: 'Gilroy-Bold', fontSize: '1.2rem', pb: 1 }}>
                            Mirror Repository
                            <IconButton aria-label="close" onClick={handleCloseModal} disabled={isSubmitting} sx={{ position: 'absolute', right: 12, top: 12, color: (theme) => theme.palette.grey[600] }} >
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
                            <Typography sx={{ mb: 1, fontFamily: 'Gilroy-Medium', fontSize: '16px', color: "#333333" }}>
                                Fill the below details to mirror a repository
                            </Typography>
                            <Typography sx={{ mb: 2.5, fontFamily: 'Gilroy-Regular', fontSize: '12px', color: '#8191A8' }}>
                                Fields marked with <span style={{ color: CHIP_COLOR_RED }}>*</span> are mandatory
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <CompulsoryFieldsLabel fieldName="Repository Name" />
                                    <TextField name="name" placeholder="Name" value={values.name} onChange={handleChange} onBlur={handleBlur} disabled={isSubmitting} error={touched.name && !!errors.name} {...commonInputStyles(errors, touched, "name")} />
                                    <StyledErrorMessage name="name" />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <CompulsoryFieldsLabel fieldName="Source Repository URL" />
                                    <TextField name="sourceRepo" placeholder="Url" type="text" value={values.sourceRepo} onChange={handleChange} onBlur={handleBlur} disabled={isSubmitting} error={touched.sourceRepo && !!errors.sourceRepo} {...commonInputStyles(errors, touched, "sourceRepo")} />
                                    <StyledErrorMessage name="sourceRepo" />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <CompulsoryFieldsLabel fieldName="Destination Repository URL" />
                                    <TextField name="destinationRepo" placeholder="Url" type="text" value={values.destinationRepo} onChange={handleChange} onBlur={handleBlur} disabled={isSubmitting} error={touched.destinationRepo && !!errors.destinationRepo} {...commonInputStyles(errors, touched, "destinationRepo")} />
                                    <StyledErrorMessage name="destinationRepo" />
                                </Grid>
                                <Grid item xs={12}>
                                    <CompulsoryFieldsLabel fieldName="Access Token" />
                                    <TextField name="token" placeholder="Token" type="password" value={values.token} onChange={handleChange} onBlur={handleBlur} disabled={isSubmitting} error={touched.token && !!errors.token} {...commonInputStyles(errors, touched, "token")} />
                                    <StyledErrorMessage name="token" />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <CompulsoryFieldsLabel fieldName="Branch Mode" />
                                    <FormControl fullWidth  size="small" variant="filled" disabled={isSubmitting} error={touched.branchMode && !!errors.branchMode}
                                        sx={{
                                            ...commonInputStyles(errors, touched, 'branchMode').sx,
                                            "& .MuiSelect-select.MuiFilledInput-input": {
                                                paddingTop: '9px', 
                                                paddingBottom: '9px',
                                            }
                                        }}
                                    >
                                        <Field 
                                            as={Select}  
                                            name="branchMode" 
                                            id="branch-mode" 
                                            // value={values.branchMode} // Formik's Field component typically handles the value prop
                                            onChange={(e: SelectChangeEvent<string>) => {
                                                handleChange(e); // Propagate change to Formik
                                                if (e.target.value === 'all') { 
                                                    setFieldValue('branchName', ''); 
                                                }
                                            }}
                                            onBlur={handleBlur} // Propagate blur to Formik
                                            disableUnderline
                                            displayEmpty // <-- Add this prop
                                            sx={{ ...commonInputStyles(errors, touched, 'branchMode').InputProps?.sx }} 
                                        >
                                            {/* Placeholder MenuItem */}
                                            <MenuItem value="" disabled>
                                                <Typography component="span" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary' }}>
                                                    Select Branch Mode
                                                </Typography>
                                            </MenuItem>
                                            {/* Actual options */}
                                            <MenuItem value="all" sx={{ fontFamily: 'Gilroy-Regular' }}>All Branches</MenuItem>
                                            <MenuItem value="custom" sx={{ fontFamily: 'Gilroy-Regular' }}>Specific Branch</MenuItem>
                                        </Field>
                                    </FormControl>
                                    <StyledErrorMessage name="branchMode" />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <CompulsoryFieldsLabel fieldName="Branch Name" />
                                    <TextField name="branchName" value={values.branchName} placeholder="branch" onChange={handleChange} onBlur={handleBlur}
                                        disabled={isSubmitting || values.branchMode === 'all'}
                                        required={values.branchMode === 'custom'}
                                        error={touched.branchName && !!errors.branchName}
                                        {...commonInputStyles(errors, touched, "branchName")} />
                                    <StyledErrorMessage name="branchName" />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <CompulsoryFieldsLabel fieldName="Tag Mode" />
                                    <FormControl fullWidth size="small" variant="filled" disabled={isSubmitting} error={touched.tagMode && !!errors.tagMode}
                                        sx={{
                                            ...commonInputStyles(errors, touched, 'tagMode').sx,
                                            "& .MuiSelect-select.MuiFilledInput-input": {
                                                paddingTop: '9px', 
                                                paddingBottom: '9px',
                                            }
                                        }}
                                    >
                                        <Field 
                                            as={Select} 
                                            name="tagMode" 
                                            id="tag-mode" 
                                            // value={values.tagMode} // Formik's Field component handles the value prop
                                            onChange={(e: SelectChangeEvent<string>) => {
                                                handleChange(e); // Propagate change to Formik
                                                if (e.target.value === 'all') { 
                                                    setFieldValue('tagName', ''); 
                                                }
                                            }}
                                            onBlur={handleBlur} // Propagate blur to Formik
                                            disableUnderline
                                            displayEmpty // <-- Add this prop
                                            sx={{ ...commonInputStyles(errors, touched, 'tagMode').InputProps?.sx }} 
                                        >
                                            {/* Placeholder MenuItem */}
                                            <MenuItem value="" disabled>
                                                <Typography component="span" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary' }}>
                                                    Select Tag Mode 
                                                </Typography>
                                            </MenuItem>
                                            {/* Actual options */}
                                            <MenuItem value="all" sx={{ fontFamily: 'Gilroy-Regular' }}>All Tags</MenuItem>
                                            <MenuItem value="custom" sx={{ fontFamily: 'Gilroy-Regular' }}>Specific Tag</MenuItem>
                                        </Field>
                                    </FormControl>
                                    <StyledErrorMessage name="tagMode" />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <CompulsoryFieldsLabel fieldName="Tag Name" />
                                    <TextField name="tagName" value={values.tagName} onChange={handleChange} placeholder="tag" onBlur={handleBlur}
                                        disabled={isSubmitting || values.tagMode === 'all'}
                                        required={values.tagMode === 'custom'}
                                        error={touched.tagName && !!errors.tagName}
                                        {...commonInputStyles(errors, touched, "tagName")} />
                                    <StyledErrorMessage name="tagName" />
                                </Grid>
                                {status?.error && (
                                    <Grid item xs={12}>
                                        <Typography color="error" variant="body2" sx={{ fontFamily: 'Gilroy-Regular', textAlign: 'center', mt: 1 }}> {status.error} </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </DialogContent>
                        <DialogActions sx={{ p: '16px 24px' }}>
                            <Button type="submit" variant="contained" disabled={isSubmitting || !dirty || !isValid || isViewer}
                                sx={{ fontFamily: 'Gilroy-Bold', bgcolor: '#101010', color: '#ffffff', textTransform: 'none', px: 3, py: 1, '&:hover': { bgcolor: '#333' } }} >
                                {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Mirror Repository'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}
            </Formik>
        </>
    );
};

const MirroringAppDetails: React.FC = () => {
    const theme = useTheme();

    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
    };

    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer
                index={"integrations"}
                onStateChange={handleDrawerStateChange}
            />
            <MiniBot/>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 0,
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    marginLeft: `${currentMarginLeft}vh`,
                }}
            >
                <MirroringAppDetailsContent />
            </Box>
        </Box>
    );
};

export default MirroringAppDetails;